import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { db, firebaseConfig } from '../firebase/config/firebase';
import { StaffAccount, AdminRole } from '../types';
import { auditService } from './auditService';

const LOCAL_STAFF_KEY = 'oto_dev_staff_accounts';

const getLocalStaffBackup = (): StaffAccount[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(LOCAL_STAFF_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveLocalStaffBackup = (list: StaffAccount[]) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(LOCAL_STAFF_KEY, JSON.stringify(list));
  } catch {}
};

export interface CreateStaffInput {
  fullName: string;
  username: string;
  email?: string;
  password?: string;
  phoneNumber?: string;
  role?: AdminRole;
  allowedSections?: string[];
  createdBy?: string;
}

export const staffService = {
  /**
   * 1. Get all staff members (Operators & Admins)
   */
  async getAllStaff(_forceRefresh = false): Promise<StaffAccount[]> {
    try {
      const q = query(collection(db, 'admins'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const list: StaffAccount[] = [];

      snap.forEach((d) => {
        const data = d.data();
        list.push({
          uid: d.id,
          fullName: data.fullName || data.displayName || 'موظف تشغيل',
          username: data.username || data.email?.split('@')[0] || d.id,
          email: data.email || `${d.id}@otostudents.com`,
          role: (data.role as AdminRole) || 'OPERATOR',
          phoneNumber: data.phoneNumber || '',
          status: (data.status as 'ACTIVE' | 'SUSPENDED') || 'ACTIVE',
          password: data.password || '',
          allowedSections: data.allowedSections || [
            'DASHBOARD',
            'BOOKINGS',
            'TRIPS',
            'STUDENTS',
            'CREATE_STUDENT',
            'DEMAND',
            'VEHICLES',
            'UNIVERSITIES',
          ],
          createdAt: data.createdAt || new Date().toISOString(),
          createdBy: data.createdBy || 'مدير المنظومة',
          lastLoginAt: data.lastLoginAt,
        });
      });

      if (list.length > 0) {
        saveLocalStaffBackup(list);
        return list;
      }
    } catch (err) {
      console.warn('Error loading staff from Firestore, fallback to local backup:', err);
    }

    return getLocalStaffBackup();
  },

  /**
   * 2. Create a new Operational Staff Account
   * Uses an isolated secondary Firebase App instance so the current Admin remains logged in.
   * Creates the real user in Firebase Auth and binds it to admins/{realUid} for cross-device login.
   */
  async createStaff(input: CreateStaffInput): Promise<StaffAccount> {
    const cleanUsername = input.username.trim().toLowerCase().replace(/\s+/g, '_');
    const cleanEmail = (input.email?.trim().toLowerCase() || `${cleanUsername}@otostudents.com`).trim();
    const cleanPassword = input.password?.trim() || 'OtoStaff@2026';
    const nowIso = new Date().toISOString();

    let realUid = '';

    // 1. Create real Firebase Auth user via isolated secondary app so current admin is NOT logged out
    try {
      const secondaryApp =
        getApps().find((a) => a.name === 'StaffBootstrapApp') ||
        initializeApp(firebaseConfig, 'StaffBootstrapApp');
      const secondaryAuth = getAuth(secondaryApp);
      const userCred = await createUserWithEmailAndPassword(
        secondaryAuth,
        cleanEmail,
        cleanPassword
      );
      realUid = userCred.user.uid;
      await signOut(secondaryAuth);
    } catch (authErr: any) {
      if (authErr?.code === 'auth/email-already-in-use') {
        console.warn('Staff email already in Firebase Auth, updating record in Firestore');
      } else {
        console.warn('Staff Firebase Auth creation note:', authErr);
      }
    }

    const staffId = realUid || `staff_${Date.now()}`;
    const staffRef = doc(db, 'admins', staffId);

    const newStaff: StaffAccount = {
      uid: staffId,
      fullName: input.fullName.trim(),
      username: cleanUsername,
      email: cleanEmail,
      phoneNumber: input.phoneNumber?.trim() || '',
      role: input.role || 'OPERATOR',
      status: 'ACTIVE',
      password: cleanPassword,
      allowedSections: input.allowedSections || [
        'DASHBOARD',
        'BOOKINGS',
        'TRIPS',
        'STUDENTS',
        'CREATE_STUDENT',
        'DEMAND',
        'VEHICLES',
        'UNIVERSITIES',
      ],
      createdAt: nowIso,
      createdBy: input.createdBy || 'admin@otostudents.com',
    };

    try {
      await setDoc(staffRef, {
        fullName: newStaff.fullName,
        displayName: newStaff.fullName,
        username: newStaff.username,
        email: newStaff.email,
        phoneNumber: newStaff.phoneNumber,
        role: newStaff.role,
        status: newStaff.status,
        password: newStaff.password,
        allowedSections: newStaff.allowedSections,
        createdAt: newStaff.createdAt,
        createdBy: newStaff.createdBy,
        uid: staffId,
      }, { merge: true });
    } catch (err) {
      console.warn('Firestore setDoc failed for staff, saving locally:', err);
    }

    // Update local cache
    const current = getLocalStaffBackup();
    const updated = [newStaff, ...current.filter((s) => s.uid !== staffId)];
    saveLocalStaffBackup(updated);

    // Audit log
    try {
      await auditService.logAction(
        'admin',
        input.createdBy || 'admin@otostudents.com',
        'STAFF_CREATE',
        'STAFF',
        staffId,
        {
          fullName: newStaff.fullName,
          username: newStaff.username,
          role: newStaff.role,
        }
      );
    } catch {}

    return newStaff;
  },

  /**
   * 3. Update staff details
   */
  async updateStaff(
    uid: string,
    updates: Partial<StaffAccount>,
    adminEmail = 'admin@otostudents.com'
  ): Promise<boolean> {
    try {
      const docRef = doc(db, 'admins', uid);
      await updateDoc(docRef, {
        ...updates,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.warn('Firestore updateDoc failed for staff, updating locally:', err);
    }

    const current = getLocalStaffBackup();
    const updated = current.map((s) => (s.uid === uid ? { ...s, ...updates } : s));
    saveLocalStaffBackup(updated);

    try {
      await auditService.logAction('admin', adminEmail, 'STAFF_UPDATE', 'STAFF', uid, updates);
    } catch {}

    return true;
  },

  /**
   * 4. Toggle staff status (ACTIVE / SUSPENDED)
   */
  async toggleStaffStatus(
    uid: string,
    nextStatus: 'ACTIVE' | 'SUSPENDED',
    adminEmail = 'admin@otostudents.com'
  ): Promise<boolean> {
    try {
      const docRef = doc(db, 'admins', uid);
      await updateDoc(docRef, {
        status: nextStatus,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.warn('Firestore updateDoc failed, updated locally:', err);
    }

    const current = getLocalStaffBackup();
    const updated = current.map((s) => (s.uid === uid ? { ...s, status: nextStatus } : s));
    saveLocalStaffBackup(updated);

    try {
      await auditService.logAction('admin', adminEmail, 'STAFF_STATUS_UPDATE', 'STAFF', uid, {
        status: nextStatus,
      });
    } catch {}

    return true;
  },

  /**
   * 5. Change staff password
   */
  async changeStaffPassword(
    uid: string,
    newPasswordValue: string,
    adminEmail = 'admin@otostudents.com'
  ): Promise<boolean> {
    try {
      const docRef = doc(db, 'admins', uid);
      await updateDoc(docRef, {
        password: newPasswordValue,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      console.warn('Firestore updateDoc failed, updated locally:', err);
    }

    const current = getLocalStaffBackup();
    const updated = current.map((s) => (s.uid === uid ? { ...s, password: newPasswordValue } : s));
    saveLocalStaffBackup(updated);

    try {
      await auditService.logAction('admin', adminEmail, 'STAFF_UPDATE', 'STAFF', uid, {
        note: 'Password changed by administrator',
      });
    } catch {}

    return true;
  },

  /**
   * 6. Delete staff account
   */
  async deleteStaff(uid: string, adminEmail = 'admin@otostudents.com'): Promise<boolean> {
    try {
      const docRef = doc(db, 'admins', uid);
      await deleteDoc(docRef);
    } catch (err) {
      console.warn('Firestore deleteDoc failed, deleted locally:', err);
    }

    const current = getLocalStaffBackup();
    const updated = current.filter((s) => s.uid !== uid);
    saveLocalStaffBackup(updated);

    try {
      await auditService.logAction('admin', adminEmail, 'STAFF_DELETE', 'STAFF', uid, {});
    } catch {}

    return true;
  },
};
